import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Rocket, BarChart3, Wrench, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const services = [
  {
    icon: Rocket,
    title: "Build Your Product & Get Customers",
    timeline: "3-6 weeks + Post-Launch",
    description: "We validate your idea, build your MVP, and get your first paying customers. You end up with a seed-funding-ready product.",
    outcomes: [
      "Validated problem-solution fit",
      "We build your MVP (not just advise)",
      "Marketing and sales execution",
      "First paying customers",
      "Seed-funding ready handover",
    ],
    ideal: "You have an idea. You want a product with paying customers, without building a team first.",
  },
  {
    icon: BarChart3,
    title: "Scale What's Working",
    timeline: "Ongoing",
    description: "For founders who have a validated product and need help scaling without the chaos.",
    outcomes: [
      "Growth strategy execution",
      "Product iteration and expansion",
      "Operational systems",
      "Team building support",
      "Sustainable scaling",
    ],
    ideal: "You've got traction. Now you need to scale responsibly without burning out or burning cash.",
  },
  {
    icon: Wrench,
    title: "Revenue-First SaaS & Internal Tools",
    timeline: "Varies",
    description: "For founders who need software that generates revenue or removes operational burden.",
    outcomes: [
      "Custom SaaS development",
      "Internal tool automation",
      "AI integration where helpful",
      "Products built to grow",
      "No over-engineering",
    ],
    ideal: "You need something built, and you want it built right, not just built fast.",
  },
];

// Person at desk with laptop illustration
const illustration = "/images/what-we-do.png";

export default function WhatWeDoPage() {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="bg-hero py-16 sm:py-20 lg:py-24">
        <div className="container-narrow">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center"
          >
            <h1 className="heading-display text-foreground mb-6">
              What We Do
            </h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto">
              We build your product, get your first paying customers, and make it seed-funding ready. Then you choose what's next.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro with Illustration */}
      <section className="bg-background py-12 sm:py-16">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7 }}
              className="flex justify-center order-2 lg:order-1"
            >
              <div className="w-full max-w-sm">
                <img
                  src={illustration}
                  alt="Founder at work - focused execution"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="order-1 lg:order-2"
            >
              <h2 className="heading-section text-foreground mb-5">Execution, Not Just Advice</h2>
              <p className="body-base text-muted-foreground mb-4">
                We do not just tell you what to do. We work alongside you, in the trenches, helping you move from uncertainty to clarity to results.
              </p>
              <p className="body-base text-muted-foreground">
                Whether you are validating a new idea or scaling what works, we bring the execution support that lets you focus on what matters most.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-section py-16 sm:py-20 lg:py-24">
        <div className="container-wide">
          <div className="space-y-6">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Card className="card-elevated overflow-hidden">
                  <div className="grid lg:grid-cols-3 gap-0">
                    <CardHeader className="lg:col-span-1 bg-muted/30 p-6 lg:p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="icon-container">
                          <service.icon className="h-6 w-6 text-accent" />
                        </div>
                        <Badge variant="outline" className="text-muted-foreground">
                          {service.timeline}
                        </Badge>
                      </div>
                      <CardTitle className="font-serif text-xl font-medium text-foreground">
                        {service.title}
                      </CardTitle>
                      <p className="text-muted-foreground mt-2">{service.description}</p>
                    </CardHeader>
                    
                    <CardContent className="lg:col-span-2 p-6 lg:p-8">
                      <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-4">What You Get</h4>
                      <ul className="grid sm:grid-cols-2 gap-3 mb-5">
                        {service.outcomes.map((outcome, j) => (
                          <li key={j} className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0" />
                            <span className="text-foreground text-sm">{outcome}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <div className="border-t border-border/50 pt-4 mt-4">
                        <p className="text-sm text-muted-foreground italic">
                          <span className="text-foreground font-medium">Ideal for:</span> {service.ideal}
                        </p>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Note */}
      <section className="bg-background py-12 sm:py-16">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-card border border-border/50 rounded-xl p-8 text-center"
          >
            <h3 className="font-serif text-xl font-medium text-foreground mb-4">A Note on AI</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              We use AI where it genuinely removes burden, not because it is trendy. If AI makes your product better or your work easier, we will use it. If it does not, we will not pretend it does.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground py-16 sm:py-20">
        <div className="container-narrow text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-section mb-5">Not Sure Which Fits?</h2>
            <p className="body-base text-primary-foreground/80 mb-6">
              Tell us where you are. We will help you figure out the right path.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 rounded-full">
                <Link to="/contact">
                  Submit Your Wishlist
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 rounded-full">
                <Link to="/engagements">See Engagement Types</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
